import _ from 'lodash'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { Stripe, StripeElements } from '@stripe/stripe-js'

import { ConsultationStatus, ConsultationType, CreateConsultationDto } from '@/core/models/consultation.model'

import useInterval from '@/hooks/useInterval'
import { countAvailableDoctors, toDoctorIds, useRoomDoctorStatuses } from '@/hooks/useRoomDoctorStatuses'

import { useAppDispatch } from '@/redux/rootReducer'
import { enterWaitingRoomSocketEvent, leaveWaitingRoomSocketEvent } from '@/redux/reducers/waiting-room.reducer'

import ReserveConsultationDialog, { ReserveConsultationFormValues } from '../components/ReserveConsultationDialog'
import PayConsultationDialog from '../components/PayConsultationDialog'
import { useGetWaitingRoomQuery } from '@/core/services/waiting-room'
import { skipToken } from '@reduxjs/toolkit/query'
import {
  useCreateConsultationMutation,
  useGetConsultationQuery,
  useGetConsultationReasonsQuery,
  useUpdateConsultationMutation,
} from '@/core/services/consultation'
import { useGetStaffQuery } from '@/core/services/staff'
import { useSearchPatientsQuery } from '@/core/services/patient'
import { PatientSourceEnum } from '@/core/enums/patient-source.enum'
import { getApiErrorMessage } from '@/utils/errors'
import { useConfirmPaymentMutation, useStartPaymentByReceiverIDMutation } from '@/core/services/charge'
import { getMinutesFromTimeString } from '@/utils/helpers'

export default function ReserveConsultationDialogContainer() {
  const appDispatch = useAppDispatch()
  const navigate = useNavigate()
  const { waitingRoomId } = useParams<{ waitingRoomId: string }>()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [currentTime, setCurrentTime] = useState(dayjs())
  const [isProcessingPayment, setIsProcessingPayment] = useState(false)
  const [openPaymentDialog, setOpenPaymentDialog] = useState<boolean>(false)

  useInterval(() => {
    setCurrentTime(dayjs())
  }, 60 * 1000)

  const [searchPatientsArgs, setSearchPatientsArgs] = useState({ patientSource: PatientSourceEnum.MedconnectPatient })

  const { data: consultationReasons } = useGetConsultationReasonsQuery({})
  const { data: patients } = useSearchPatientsQuery(searchPatientsArgs, { selectFromResult: ({ data, ...rest }) => ({ ...rest, data: data?.items }) })
  const { data: waitingRoom, isLoading: isFetchingWaitingRoom } = useGetWaitingRoomQuery(waitingRoomId ?? skipToken)
  const { data: lastQueuedConsultation } = useGetConsultationQuery(_.last(waitingRoom?.consultations)?._id ?? skipToken)
  const { data: paymentReceiver } = useGetStaffQuery(waitingRoom?.paymentReceiverId ?? skipToken)
  
  const [createConsultation, { data: newlyCreatedConsultation, isLoading: isReserving }] = useCreateConsultationMutation()
  const [updateConsultation] = useUpdateConsultationMutation()
  const [acceptPaymentFromUserID] = useStartPaymentByReceiverIDMutation()
  const [confirmPayment] = useConfirmPaymentMutation()

  const { statuses: doctorStatuses, isReady: doctorStatusesReady } = useRoomDoctorStatuses(waitingRoomId)

  const hasAvailableDoctor = useMemo(
    () => doctorStatusesReady && countAvailableDoctors(toDoctorIds(waitingRoom?.doctors), doctorStatuses) > 0,
    [doctorStatuses, doctorStatusesReady, waitingRoom]
  )

  const canReserveConsultation = useMemo(() => {
    if (!waitingRoom) return false

    const currentOpeningHour = _.find(waitingRoom.openingHours, ({ day }) => day === currentTime.day())
    const currentPeriod = _.find(currentOpeningHour?.periods, (period) => {
      const currentTimeInMinutes = currentTime.get('hours') * 60 + currentTime.get('minutes')
      return (
        currentTimeInMinutes >= dayjs.duration(getMinutesFromTimeString(period.open), 'minutes').asMinutes() &&
        currentTimeInMinutes <= dayjs.duration(getMinutesFromTimeString(period.close), 'minutes').asMinutes()
      )
    })

    if (!currentPeriod) return false

    if (!lastQueuedConsultation)
      return currentTime.add(waitingRoom.avgWaitingTime, 'minutes').isSameOrAfter(dayjs(currentPeriod.close, 'HH:mm'))
        ? hasAvailableDoctor
        : true

    return dayjs(lastQueuedConsultation.startDate)
      .add(waitingRoom.avgWaitingTime * 2, 'minutes')
      .isSameOrAfter(dayjs(currentPeriod.close, 'HH:mm'))
      ? hasAvailableDoctor
      : true
  }, [currentTime, hasAvailableDoctor, lastQueuedConsultation, waitingRoom])

  const patientOptions = useMemo(
    () =>
      _.map(patients, (patient) => ({
        label: `${patient.userInfo.name} ${patient.userInfo.surname}`,
        value: patient._id,
      })),
    [patients]
  )

  const reasonOptions = useMemo(() => _.map(consultationReasons, 'name'), [consultationReasons])

  const specialityOptions = useMemo(
    () =>
      _.map(waitingRoom?.specialities, (speciality) => ({
        label: speciality.name,
        value: speciality.name,
      })),
    [waitingRoom]
  )

  const handleSearchPatient = useDebouncedCallback((value: string) => {
    setSearchPatientsArgs((prevArgs) => ({ ...prevArgs, smartSearch: value || undefined }))
  }, 300)

  const handleReserveConsultation = async ({ patient: patientId, speciality, reason }: ReserveConsultationFormValues) => {
    if (!waitingRoom) return

    const createConsultationDto: CreateConsultationDto = {
      patient: patientId,
      speciality,
      startDate: dayjs().toISOString(),
      status: ConsultationStatus.Scheduled,
      type: ConsultationType.Classic,
      waitingRoom: waitingRoom._id,
      price: waitingRoom.price,
    }

    if (reason) createConsultationDto.reason = reason

    try {
      const consultation = await createConsultation(createConsultationDto).unwrap()
      enqueueSnackbar(t('notification.consultation.create.success'), { variant: 'success' })
      // if waiting room has payment enabled, open payment dialog
      if (waitingRoom.price !== 0) {
        setOpenPaymentDialog(true)
      } else {
        navigate(`/consultations/${consultation._id}/live`)
      }
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.consultation.create.error')), { variant: 'error' })
    }
  }

  const handleStartPayment = async (stripe: Stripe, elements: StripeElements) => {
    if (!newlyCreatedConsultation || !waitingRoom?.paymentReceiverId) return
    const { _id: consultationId, patient } = newlyCreatedConsultation
    try {
      setIsProcessingPayment(true)
      const paymentIntent = await acceptPaymentFromUserID({ consultationId, receiverID: waitingRoom.paymentReceiverId }).unwrap()
      const { client_secret: clientSecret } = paymentIntent

      if (!clientSecret) throw new Error('client secret is null')

      const { error } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement('cardNumber')!,
          billing_details: {
            name: `${patient.userInfo.name} ${patient.userInfo.surname}`,
          },
        },
      })

      if (error) throw error

      await confirmPayment(consultationId).unwrap()
      setOpenPaymentDialog(false)
      enqueueSnackbar(t('notification.consultation.acceptPayment.success'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.consultation.acceptPayment.error')), { variant: 'error' })
    } finally {
      setIsProcessingPayment(false)
    }
  }

  const handleQuitOnPaymentDialog = () => {
    setOpenPaymentDialog(false)
    if (!newlyCreatedConsultation) return
    // delete created consultation ???
    updateConsultation({ id: newlyCreatedConsultation._id, changes: { status: ConsultationStatus.Canceled } }).then(() => {
      enqueueSnackbar(t('notification.consultation.canceled.success'), { variant: 'info' })
    })
  }

  return (
    <>
      <ReserveConsultationDialog
        canReserve={canReserveConsultation}
        isLoading={isFetchingWaitingRoom}
        isReserving={isReserving}
        onSearchPatient={handleSearchPatient}
        onReserveConsultation={handleReserveConsultation}
        patientOptions={patientOptions}
        reasonOptions={reasonOptions}
        specialityOptions={specialityOptions}
        isPaid={waitingRoom?.price !== 0}
      />
      {newlyCreatedConsultation && waitingRoom?.paymentReceiverId && (
        <PayConsultationDialog
          isPaymentReceiverStripeActivated={!!paymentReceiver?.stripeActivated}
          open={openPaymentDialog}
          onClose={handleQuitOnPaymentDialog}
          onPaymentStart={handleStartPayment}
          paymentLoading={isProcessingPayment}
          price={newlyCreatedConsultation?.price || 0}
        />
      )}
    </>
  )
}
